import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollContainer, getScrollEventTarget, getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * @typedef {object} SearchPaginationContext
 * @property {string} url
 * @property {string} serverContext
 * @property {HTMLUListElement | undefined} grid
 * @property {boolean} ready
 * @property {Map<number, Promise<void>>} requests
 * @property {Set<number>} rendering
 * @property {AbortController} controller
 * @property {number} previousIntentUntil
 * @property {boolean} previousPending
 * @property {number} previousIntentVersion
 */

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper | undefined} */
  #aspectRatioHelper;

  /**
   * Each search refresh owns its requests. Object identity also separates equal-URL refreshes.
   * @type {SearchPaginationContext | undefined}
   */
  #searchContext;

  /** @type {{ promise: Promise<unknown>, url: string } | undefined} */
  #pendingSearchRefresh;

  /** @type {AbortController | undefined} */
  #searchIntentListeners;
  /** @type {number | undefined} */
  #searchTouchY;
  #searchScrollTop = 0;

  connectedCallback() {
    super.connectedCallback();

    if (this.matches('.gc-search-results')) {
      const context = this.#resetSearchContext(!this.#pendingSearchRefresh);
      if (this.#pendingSearchRefresh) this.#completeSearchRefresh(context, this.#pendingSearchRefresh);
      this.#listenForSearchIntent();
    }

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
    this.#updateSearchPreviousLink();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.matches('.gc-search-results')) {
      this.#searchIntentListeners?.abort();
      this.#clearSearchIntent();
      this.#searchContext?.controller.abort();
      this.#searchContext = undefined;
      this.pages.clear();
    }
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#updateSearchPreviousLink();
    const context = this.#searchContext;
    if (!this.matches('.gc-search-results') || !this.isConnected || !context?.ready || this.#pendingSearchRefresh) return;
    // A locale/currency morph can change the server context without changing the URL.
    // Filter morphs instead resume after their published render promise completes.
    if (context.url !== this.#searchURL() || context.serverContext === (this.dataset.gcSearchContext || '')) return;
    this.#resetSearchContext();
    this.#observeViewMore();
    this.#fetchPage('next');
    this.#fetchPage('previous');
  }

  #observeViewMore() {
    if (this.matches('.gc-search-results') && !this.#isCurrentSearch(this.#searchContext)) return;
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          // Render the next page well before the user reaches the bottom
          // (was 100px, so a slow section fetch stalled visibly at the end
          // of the current page). 1000px gives the fetch time to finish
          // while the user is still scrolling through the current page.
          rootMargin: '1000px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    if (this.matches('.gc-search-results')) {
      const context = this.#searchContext;
      if (page && context && this.#isCurrentSearch(context)) await this.#fetchSearchPage(page, context);
      return;
    }

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    if (this.matches('.gc-search-results')) return this.#renderSearchPage('next');

    const currentUrl = window.location.href;
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    await yieldToMainThread();

    if (this.matches('.gc-search-results')) {
      // A pending page must not rewrite a different filter/history entry.
      if (this.isConnected && window.location.href === currentUrl) {
        history.replaceState(history.state, '', nextPage.url.toString());
      }
    } else {
      history.pushState('', '', nextPage.url.toString());
    }

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    if (this.matches('.gc-search-results')) return this.#renderSearchPage('previous');

    const currentUrl = window.location.href;
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const currentScrollTop = getScrollTop();
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
      const heightDiff = newHeight - oldHeight;
      scrollTo({
        top: currentScrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    await yieldToMainThread();

    if (this.matches('.gc-search-results')) {
      // A pending page must not rewrite a different filter/history entry.
      if (this.isConnected && window.location.href === currentUrl) {
        history.replaceState(history.state, '', previousPage.url.toString());
      }
    } else {
      history.pushState('', '', previousPage.url.toString());
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  #searchURL() {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.hash = '';
    return url.toString();
  }

  #updateSearchPreviousLink() {
    if (!this.matches('.gc-search-results')) return;
    const link = this.querySelector('[data-gc-search-previous]');
    if (!(link instanceof HTMLAnchorElement)) return;
    const previous = this.#getPage('previous');
    link.hidden = !previous || !this.#shouldUsePage(previous);
    if (!link.hidden && previous) link.href = previous.url.toString();
    else link.removeAttribute('href');
  }

  #listenForSearchIntent() {
    this.#searchIntentListeners?.abort();
    this.#searchIntentListeners = new AbortController();
    const options = { capture: true, passive: true, signal: this.#searchIntentListeners.signal };
    document.addEventListener('wheel', this.#searchWheel, options);
    document.addEventListener('touchstart', this.#searchTouchStart, options);
    document.addEventListener('touchmove', this.#searchTouchMove, options);
    document.addEventListener('touchend', this.#searchTouchEnd, options);
    document.addEventListener('touchcancel', this.#searchTouchEnd, options);
    document.addEventListener('keydown', this.#searchKey, options);
    // Capture sees the desktop .page-wrapper's non-bubbling scroll events too.
    document.addEventListener('scroll', this.#searchScroll, options);
    window.addEventListener('pageshow', this.#clearSearchIntent, options);
    window.addEventListener('popstate', this.#clearSearchIntent, options);
  }

  #clearSearchIntent = () => {
    this.#cancelPreviousIntent();
    this.#searchTouchY = undefined;
    this.#searchScrollTop = getScrollTop();
  };

  #cancelPreviousIntent() {
    if (this.#searchContext) {
      this.#searchContext.previousIntentUntil = 0;
      this.#searchContext.previousIntentVersion++;
    }
  }

  /** @param {Event} event */
  #isMainScrollGesture(event) {
    if (!event.isTrusted || event.defaultPrevented) return false;
    const target = event.target;
    if (!(target instanceof Element)) return target === document;
    if (target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="textbox"], dialog, [role="dialog"], [role="menu"]')) return false;
    const container = getScrollContainer();
    for (let node = target; node && node !== container && node !== document.documentElement; node = node.parentElement) {
      if (node.scrollHeight > node.clientHeight && /^(auto|scroll|overlay)$/.test(getComputedStyle(node).overflowY)) return false;
    }
    return true;
  }

  #searchPreviousIsNear() {
    const sentinel = this.refs.viewMorePrevious;
    if (!sentinel) return false;
    const rect = sentinel.getBoundingClientRect();
    const container = getScrollContainer();
    const root = container === document.scrollingElement || container === document.documentElement
      ? { top: 0, bottom: window.innerHeight }
      : container.getBoundingClientRect();
    // The native sentinel can have zero height. Match the existing 1000px prefetch margin.
    return rect.bottom >= root.top - 1000 && rect.top <= root.bottom + 1000;
  }

  #allowPreviousFromGesture() {
    const context = this.#searchContext;
    if (!context || !this.#isCurrentSearch(context) || context.previousPending) return;
    // A short token permits Home/large upward gestures to reach the sentinel,
    // without leaving permission behind for later programmatic restoration.
    context.previousIntentUntil = Date.now() + 1200;
    if (this.#searchPreviousIsNear()) this.#renderPreviousPage();
  }

  /** @param {WheelEvent} event */
  #searchWheel = (event) => {
    if (!this.#isMainScrollGesture(event) || event.ctrlKey) return;
    if (event.deltaY > 0) this.#cancelPreviousIntent();
    else if (event.deltaY < 0) this.#allowPreviousFromGesture();
  };

  /** @param {TouchEvent} event */
  #searchTouchStart = (event) => {
    this.#searchTouchY = this.#isMainScrollGesture(event) && event.touches.length === 1 ? event.touches[0].clientY : undefined;
  };

  /** @param {TouchEvent} event */
  #searchTouchMove = (event) => {
    if (!this.#isMainScrollGesture(event) || event.touches.length !== 1 || this.#searchTouchY === undefined) return;
    const y = event.touches[0].clientY;
    const delta = y - this.#searchTouchY;
    if (Math.abs(delta) < 8) return;
    this.#searchTouchY = y;
    if (delta > 0) this.#allowPreviousFromGesture();
    else this.#cancelPreviousIntent();
  };

  #searchTouchEnd = () => { this.#searchTouchY = undefined; };

  /** @param {KeyboardEvent} event */
  #searchKey = (event) => {
    if (!this.#isMainScrollGesture(event) || event.repeat || event.altKey || event.metaKey) return;
    if (event.ctrlKey && event.key !== 'Home' && event.key !== 'End') return;
    if (event.target instanceof Element && event.target.closest('button, summary, [role="button"]')) return;
    if (['ArrowUp', 'PageUp', 'Home'].includes(event.key) || (event.key === ' ' && event.shiftKey)) this.#allowPreviousFromGesture();
    else if (['ArrowDown', 'PageDown', 'End', ' '].includes(event.key)) this.#cancelPreviousIntent();
  };

  /** @param {Event} event */
  #searchScroll = (event) => {
    if (event.target !== getScrollEventTarget() && event.target !== getScrollContainer()) return;
    const top = getScrollTop();
    const previousTop = this.#searchScrollTop;
    this.#searchScrollTop = top;
    if (top > previousTop) this.#cancelPreviousIntent();
    else if (top < previousTop && this.#searchPreviousIsNear()) this.#renderPreviousPage();
  };

  /** @param {boolean} [ready] */
  #resetSearchContext(ready = true) {
    this.#clearSearchIntent();
    this.#searchContext?.controller.abort();
    this.pages.clear();
    this.infinityScrollObserver?.disconnect();
    return (this.#searchContext = {
      url: this.#searchURL(),
      serverContext: this.dataset.gcSearchContext || '',
      grid: this.refs.grid,
      ready,
      requests: new Map(),
      rendering: new Set(),
      controller: new AbortController(),
      previousIntentUntil: 0,
      previousPending: false,
      previousIntentVersion: 0,
    });
  }

  /** @param {SearchPaginationContext | undefined} context */
  #isCurrentSearch(context) {
    return Boolean(
      context && context === this.#searchContext && context.ready && this.isConnected &&
      context.url === this.#searchURL() && context.grid === this.refs.grid &&
      context.serverContext === (this.dataset.gcSearchContext || '')
    );
  }

  /**
   * @param {{ page: number, url: URL }} page
   * @param {SearchPaginationContext} context
   */
  async #fetchSearchPage(page, context) {
    if (!this.#isCurrentSearch(context) || !this.#shouldUsePage(page) || this.pages.has(page.page)) return;
    if (context.requests.has(page.page)) return context.requests.get(page.page);

    const request = (async () => {
      try {
        // Own the request and bypass the renderer's cross-refresh HTML cache.
        // SectionRenderer also adds section_id and sorts params on the supplied URL.
        const html = await Promise.resolve().then(() =>
          sectionRenderer.getSectionHTML(this.sectionId, false, new URL(page.url), context.controller.signal)
        );
        if (this.#isCurrentSearch(context)) this.pages.set(page.page, html);
        // Auto-append each prefetched next page as soon as it arrives, so
        // search results keep loading on their own after a search without
        // waiting for scroll. Rendering a page idle-fetches the following
        // page, so this chains until the last page (out-of-range pages
        // never reach this hook). The scroll sentinel stays as a fallback
        // for any page whose fetch failed.
        if (this.#isCurrentSearch(context) && this.#getPage('next')?.page === page.page) {
          queueMicrotask(() => {
            if (this.isConnected) this.#renderSearchPage('next');
          });
        }
      } catch {
        // Leave the page uncached so the next intersection can retry.
      } finally {
        context.requests.delete(page.page);
      }
    })();
    context.requests.set(page.page, request);
    return request;
  }

  /** @param {'next' | 'previous'} type */
  async #renderSearchPage(type) {
    const context = this.#searchContext;
    const { grid } = this.refs;
    const page = this.#getPage(type);
    if (!context || !grid || !page || !this.#isCurrentSearch(context) || !this.#shouldUsePage(page)) return;
    if (context.rendering.has(page.page)) return;
    const intentVersion = context.previousIntentVersion;
    if (type === 'previous') {
      if (context.previousPending || context.previousIntentUntil < Date.now() || !this.#searchPreviousIsNear()) return;
      // Consume before awaiting: observer repeats and gesture bursts cannot queue a cascade.
      context.previousIntentUntil = 0;
      context.previousPending = true;
    }
    context.rendering.add(page.page);
    const currentUrl = window.location.href;

    try {
      await this.#fetchSearchPage(page, context);
      // Validate before touching the DOM, not just before updating history.
      if (!this.#isCurrentSearch(context) || this.#getPage(type)?.page !== page.page) return;
      if (type === 'previous' && (context.previousIntentVersion !== intentVersion || !this.#searchPreviousIsNear())) return;
      if (grid.querySelector(`:scope > [ref="cards[]"][data-page="${page.page}"]`)) return;
      const elements = this.#getGridForPage(page.page);
      if (!elements?.length) return;

      const firstElement = type === 'previous' ? grid.firstElementChild : null;
      const currentScrollTop = firstElement ? getScrollTop() : 0;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;
      if (type === 'previous') grid.prepend(...elements);
      else grid.append(...elements);
      this.#aspectRatioHelper?.processNewElements();

      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
        scrollTo({ top: currentScrollTop + newHeight - oldHeight, behavior: 'instant' });
      }

      await yieldToMainThread();
      if (!this.#isCurrentSearch(context)) return;
      this.#updateSearchPreviousLink();
      if (window.location.href === currentUrl) history.replaceState(history.state, '', page.url.toString());
      // Fetch the following page right away, not on idle: the auto-load
      // chain stalls if an idle callback is delayed, and this fetch is
      // what the next auto-render waits on.
      if (this.#isCurrentSearch(context)) this.#fetchPage(type);
    } finally {
      if (type === 'previous') context.previousPending = false;
      context.rendering.delete(page.page);
    }
  }

  /** @param {Event & { promise?: Promise<unknown> }} event */
  #refreshSearch(event) {
    const context = this.#resetSearchContext(false);
    // Facets publishes this promise before the section morph and resolves it afterwards.
    // Without it there is no safe signal that the current grid belongs to the new URL.
    if (!event.promise || typeof event.promise.then !== 'function') return;
    const pending = { promise: event.promise, url: context.url };
    this.#pendingSearchRefresh = pending;
    this.#completeSearchRefresh(context, pending);
  }

  /**
   * @param {SearchPaginationContext} context
   * @param {{ promise: Promise<unknown>, url: string }} pending
   */
  async #completeSearchRefresh(context, pending) {
    try {
      await pending.promise;
    } catch {
      return;
    }
    if (
      this.#searchContext !== context || this.#pendingSearchRefresh !== pending || !this.isConnected ||
      context.url !== pending.url || context.url !== this.#searchURL()
    ) return;
    this.#pendingSearchRefresh = undefined;
    context.grid = this.refs.grid;
    context.serverContext = this.dataset.gcSearchContext || '';
    context.ready = true;
    this.#observeViewMore();
    this.#fetchPage('next');
    this.#fetchPage('previous');
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    if (this.matches('.gc-search-results')) {
      this.#refreshSearch(event);
      return;
    }
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
